/**
 * Accounts, and resetting the passwords on them. ADMIN only.
 *
 * The screen exists because of one asymmetry: a volunteer who has forgotten
 * their password has no way back in on their own — there is no reset-by-email
 * flow, deliberately, because that would mean trusting an inbox the Foundation
 * does not control for an account that records people's hours. So an admin sets
 * a temporary one and tells them, and the account is forced to replace it at
 * the next sign-in.
 *
 * What the screen refuses to do is as important as what it does:
 *
 *   - It never shows a password. The admin types one and it goes; there is no
 *     "reveal" and nothing is stored to reveal.
 *   - It does not offer a reset on the admin's own row. Changing your own
 *     password asks for the current one, and this path does not — routing your
 *     own through here would be a way around that check.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isPasswordValid } from '@parinaam-vms/shared';
import type { PasswordStatus } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  PasswordInput,
  PasswordRules,
} from '../../components/common';
import { useAuth } from '../../hooks/useAuth';
import { apiGet, apiPost, ApiRequestError } from '../../services/api/client';
import styles from './AccountsPage.module.css';

interface Account {
  user_id: string;
  email: string;
  display_name: string;
  role: 'ADMIN' | 'TEAM' | 'VOLUNTEER';
  is_active: boolean;
  last_login_at: string | null;
  password: PasswordStatus;
}

const ROLE_LABEL: Record<Account['role'], string> = {
  ADMIN: 'Administrator',
  TEAM: 'Parinaam team',
  VOLUNTEER: 'Volunteer',
};

export function AccountsPage() {
  const { user, hasRole } = useAuth();
  const [resetting, setResetting] = useState<Account | null>(null);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => apiGet<Account[]>('/users'),
  });

  // Hiding the page is presentation; the API refuses a TEAM account outright.
  // Saying so is better than a blank screen that looks like a loading failure.
  if (!hasRole('ADMIN')) {
    return (
      <EmptyState
        title="Admins only"
        message="Account management is limited to administrator accounts."
      />
    );
  }

  if (accounts.isPending) return <LoadingState label="Loading accounts…" />;
  if (accounts.isError) {
    return <EmptyState title="Could not load accounts" message="Please try again in a moment." />;
  }

  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.title}>Accounts</h1>
        <p className={styles.subtitle}>
          Everyone who can sign in.
        </p>
      </header>

      <Card flush>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Password</th>
              <th scope="col">
                <span className={styles.visuallyHidden}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.data.map((account) => (
              <tr key={account.user_id}>
                <td>{account.display_name}</td>
                <td className={styles.email}>{account.email}</td>
                <td>{ROLE_LABEL[account.role]}</td>
                <td>
                  <PasswordCell status={account.password} />
                </td>
                <td className={styles.actionCell}>
                  {account.user_id === user?.user_id ? (
                    // Your own row. The profile page is where this is done, and
                    // it asks for the current password — which is the check
                    // this screen does not make.
                    <span className={styles.self}>Use your profile page</span>
                  ) : (
                    <Button variant="secondary" onClick={() => setResetting(account)}>
                      Reset password
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {resetting && <ResetDialog account={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

/**
 * What state this password is in, in one cell.
 *
 * The admin-set case is called out first and in words, because it is the one
 * that explains a support call: "I reset it on Tuesday and they still cannot
 * get in" is answered by seeing that it is still waiting to be replaced.
 */
function PasswordCell({ status }: { status: PasswordStatus }) {
  if (status.must_change) {
    return <span className={styles.mustChange}>Must be changed at next sign-in</span>;
  }
  if (status.days_remaining === null) return <span className={styles.never}>Does not expire</span>;
  if (status.days_remaining < 0) return <span className={styles.expired}>Expired</span>;

  return (
    <span className={status.days_remaining <= 5 ? styles.expiringSoon : undefined}>
      {status.days_remaining === 0
        ? 'Expires today'
        : `${status.days_remaining} day${status.days_remaining === 1 ? '' : 's'} left`}
    </span>
  );
}

function ResetDialog({ account, onClose }: { account: Account; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: (newPassword: string) =>
      apiPost(`/users/${account.user_id}/password`, { new_password: newPassword }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Could not reset the password.'
      ),
  });

  return (
    <div className={styles.dialogBackdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="reset-title" className={styles.dialogTitle}>
          Reset {account.display_name}&rsquo;s password
        </h2>
        <p className={styles.dialogLead}>
          They must choose their own next time.
        </p>

        {error && (
          <p className={styles.dialogError} role="alert">
            {error}
          </p>
        )}

        <PasswordInput
          label="Temporary password"
          required
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {/*
          The same checklist the account holder will see. A temporary
          password the API refuses is an admin on the phone to somebody who
          still cannot get in.
        */}
        <PasswordRules value={password} />

        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={reset.isPending || !isPasswordValid(password)}
            onClick={() => {
              setError(null);
              reset.mutate(password);
            }}
          >
            {reset.isPending ? 'Resetting…' : 'Reset password'}
          </Button>
        </div>
      </div>
    </div>
  );
}
